import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, vModelText as _vModelText, isRef as _isRef, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0ef0ae76"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "assistant-access" }
const _hoisted_2 = { class: "assistant-access__summary" }
const _hoisted_3 = { class: "assistant-access__heading" }
const _hoisted_4 = {
  key: 0,
  class: "assistant-access__error"
}
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "assistant-access__field" }
const _hoisted_7 = {
  value: "",
  disabled: ""
}
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "assistant-access__toggle" }
const _hoisted_10 = { class: "assistant-access__permissions" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "assistant-access__field" }
const _hoisted_14 = { value: "SELF" }
const _hoisted_15 = { value: "COMPANY" }
const _hoisted_16 = { value: "SELECTED" }
const _hoisted_17 = { value: "TENANT" }
const _hoisted_18 = { class: "assistant-access__toggle" }
const _hoisted_19 = { class: "assistant-access__muted" }
const _hoisted_20 = {
  key: 0,
  class: "assistant-access__selected"
}
const _hoisted_21 = { class: "assistant-access__field" }
const _hoisted_22 = {
  key: 0,
  class: "assistant-access__muted"
}
const _hoisted_23 = {
  key: 1,
  class: "assistant-access__search-results"
}
const _hoisted_24 = ["disabled", "onClick"]
const _hoisted_25 = { class: "assistant-access__chips" }
const _hoisted_26 = ["onClick", "aria-label"]
const _hoisted_27 = { class: "assistant-access__types" }
const _hoisted_28 = { class: "assistant-access__select-all" }
const _hoisted_29 = ["indeterminate", "disabled"]
const _hoisted_30 = ["value"]
const _hoisted_31 = {
  key: 0,
  class: "assistant-access__muted"
}

import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { Button } from "@/components/common";
import HrDataService from "@/services/hr/HrDataService";
import type {
  HrAssistantAccessSettings,
  HrAssistantEmployeeScope,
  HrAssistantReference,
} from "@/types/hr/Hr";
import { t } from "@/i18n";
import { getPermissionLabel } from "@/utils/labels";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const settings = ref<HrAssistantAccessSettings | null>(null);
const selectedRoleId = ref("");
const loading = ref(false);
const saving = ref(false);
const error = ref("");
const employeeQuery = ref("");
const employeeResults = ref<HrAssistantReference[]>([]);
const employeeSearching = ref(false);
const employeeNames = reactive<Record<string, string>>({});
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let searchSequence = 0;

const form = reactive({
  enabled: false,
  unclassifiedDocuments: false,
  employeeScope: "SELF" as HrAssistantEmployeeScope,
  employeeIds: [] as string[],
  documentTypeIds: [] as string[],
});

const selectedRole = computed(() =>
  settings.value?.roles.find((role) => role.id === selectedRoleId.value)
);
const assistantPermissions = computed(() =>
  (selectedRole.value?.permissions ?? []).filter((permission) =>
    permission.startsWith("HR_ASSISTANT_")
  )
);
const selectedEmployees = computed(() =>
  form.employeeIds.map((id) => ({ id, name: employeeNames[id] || id }))
);
const allDocumentTypesSelected = computed({
  get: () => {
    const types = settings.value?.documentTypes ?? [];
    return (
      types.length > 0 &&
      types.every((type) => form.documentTypeIds.includes(type.id))
    );
  },
  set: (selected: boolean) => {
    form.documentTypeIds = selected
      ? settings.value?.documentTypes.map((type) => type.id) ?? []
      : [];
  },
});
const someDocumentTypesSelected = computed(
  () =>
    !allDocumentTypesSelected.value &&
    Boolean(
      settings.value?.documentTypes.some((type) =>
        form.documentTypeIds.includes(type.id)
      )
    )
);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const response = await HrDataService.getAssistantAccessSettings();
    settings.value = response.data.data as HrAssistantAccessSettings;
    settings.value.selectedEmployees.forEach((employee) => {
      employeeNames[employee.id] = employee.name;
    });
    if (!selectedRoleId.value && settings.value.roles.length) {
      selectedRoleId.value = settings.value.roles[0].id;
    }
    selectRole();
  } catch (exception: unknown) {
    error.value = apiErrorMessage(exception, t("hr.assistantAccessLoadError"));
  } finally {
    loading.value = false;
  }
}

function selectRole() {
  searchSequence += 1;
  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
  employeeSearching.value = false;
  const access = selectedRole.value?.access;
  form.enabled = access?.enabled ?? false;
  form.unclassifiedDocuments = access?.unclassifiedDocuments ?? false;
  form.employeeScope = access?.employeeScope ?? "SELF";
  form.employeeIds = [...(access?.employeeIds ?? [])];
  form.documentTypeIds = [...(access?.documentTypeIds ?? [])];
  employeeQuery.value = "";
  employeeResults.value = [];
}

async function save() {
  const role = selectedRole.value;
  if (!role || saving.value) return;
  saving.value = true;
  error.value = "";
  try {
    const response = await HrDataService.updateAssistantRoleAccess(role.id, {
      enabled: form.enabled,
      unclassifiedDocuments: form.unclassifiedDocuments,
      employeeScope: form.employeeScope,
      employeeIds: form.employeeScope === "SELECTED" ? form.employeeIds : [],
      documentTypeIds: form.documentTypeIds,
    });
    role.access = response.data.data.access;
    selectRole();
  } catch (exception: unknown) {
    error.value = apiErrorMessage(exception, t("hr.assistantAccessSaveError"));
  } finally {
    saving.value = false;
  }
}

function scheduleEmployeeSearch() {
  if (searchTimer) clearTimeout(searchTimer);
  const sequence = ++searchSequence;
  const query = employeeQuery.value.trim();
  if (query.length < 2) {
    employeeResults.value = [];
    employeeSearching.value = false;
    return;
  }
  searchTimer = setTimeout(() => void searchEmployees(query, sequence), 300);
}

async function searchEmployees(query: string, sequence: number) {
  employeeSearching.value = true;
  try {
    const response = await HrDataService.searchAssistantAccessEmployees(query);
    if (sequence !== searchSequence) return;
    employeeResults.value = response.data.data as HrAssistantReference[];
    employeeResults.value.forEach((employee) => {
      employeeNames[employee.id] = employee.name;
    });
  } catch {
    if (sequence === searchSequence) employeeResults.value = [];
  } finally {
    if (sequence === searchSequence) employeeSearching.value = false;
  }
}

function addEmployee(employee: HrAssistantReference) {
  employeeNames[employee.id] = employee.name;
  if (!form.employeeIds.includes(employee.id))
    form.employeeIds.push(employee.id);
}

function removeEmployee(id: string) {
  form.employeeIds = form.employeeIds.filter((value) => value !== id);
}

function apiErrorMessage(exception: unknown, fallback: string) {
  const response = exception as { response?: { data?: { message?: string } } };
  return response.response?.data?.message || fallback;
}

onMounted(load);
onBeforeUnmount(() => {
  searchSequence += 1;
  if (searchTimer) clearTimeout(searchTimer);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("details", _hoisted_1, [
    _createElementVNode("summary", _hoisted_2, [
      _createElementVNode("h2", null, _toDisplayString(_unref(t)("hr.assistantAccessTitle")), 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("p", null, _toDisplayString(_unref(t)("hr.assistantAccessHint")), 1),
      _createVNode(_unref(Button), {
        disabled: loading.value || saving.value || !_unref(selectedRole),
        onClick: save
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(saving.value ? _unref(t)("hr.saving") : _unref(t)("common.save")), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)("hr.loading")), 1))
      : (settings.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createElementVNode("label", _hoisted_6, [
              _createElementVNode("span", null, _toDisplayString(_unref(t)("hr.assistantRole")), 1),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedRoleId).value = $event)),
                class: "input-form",
                onChange: selectRole
              }, [
                _createElementVNode("option", _hoisted_7, _toDisplayString(_unref(t)("hr.assistantChooseRole")), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(settings.value.roles, (role) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: role.id,
                    value: role.id
                  }, _toDisplayString(role.name), 9, _hoisted_8))
                }), 128))
              ], 544), [
                [_vModelSelect, selectedRoleId.value]
              ])
            ]),
            (_unref(selectedRole))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("label", _hoisted_9, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).enabled) = $event)),
                      type: "checkbox"
                    }, null, 512), [
                      [_vModelCheckbox, _unref(form).enabled]
                    ]),
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("hr.assistantEnabled")), 1)
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("strong", null, _toDisplayString(_unref(t)("hr.assistantRolePermissions")), 1),
                    (!_unref(assistantPermissions).length)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_unref(t)("hr.assistantNoPermissions")), 1))
                      : (_openBlock(), _createElementBlock("ul", _hoisted_12, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(assistantPermissions), (permission) => {
                            return (_openBlock(), _createElementBlock("li", { key: permission }, _toDisplayString(_unref(getPermissionLabel)(permission)), 1))
                          }), 128))
                        ]))
                  ]),
                  _createElementVNode("label", _hoisted_13, [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("hr.assistantEmployeeScope")), 1),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).employeeScope) = $event)),
                      class: "input-form"
                    }, [
                      _createElementVNode("option", _hoisted_14, _toDisplayString(_unref(t)("hr.assistantScopeSelf")), 1),
                      _createElementVNode("option", _hoisted_15, _toDisplayString(_unref(t)("hr.assistantScopeCompany")), 1),
                      _createElementVNode("option", _hoisted_16, _toDisplayString(_unref(t)("hr.assistantScopeSelected")), 1),
                      _createElementVNode("option", _hoisted_17, _toDisplayString(_unref(t)("hr.assistantScopeTenant")), 1)
                    ], 512), [
                      [_vModelSelect, _unref(form).employeeScope]
                    ])
                  ]),
                  _createElementVNode("label", _hoisted_18, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).unclassifiedDocuments) = $event)),
                      type: "checkbox"
                    }, null, 512), [
                      [_vModelCheckbox, _unref(form).unclassifiedDocuments]
                    ]),
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("hr.assistantUnclassifiedDocuments")), 1)
                  ]),
                  _createElementVNode("p", _hoisted_19, _toDisplayString(_unref(t)("hr.assistantUnclassifiedHint")), 1),
                  (_unref(form).employeeScope === 'SELECTED')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                        _createElementVNode("label", _hoisted_21, [
                          _createElementVNode("span", null, _toDisplayString(_unref(t)("hr.assistantEmployeeSearch")), 1),
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((employeeQuery).value = $event)),
                            class: "input-form",
                            maxlength: "100",
                            onInput: scheduleEmployeeSearch
                          }, null, 544), [
                            [
                              _vModelText,
                              employeeQuery.value,
                              void 0,
                              { trim: true }
                            ]
                          ])
                        ]),
                        (employeeSearching.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_22, _toDisplayString(_unref(t)("hr.loading")), 1))
                          : _createCommentVNode("", true),
                        (employeeResults.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(employeeResults.value, (employee) => {
                                return (_openBlock(), _createElementBlock("button", {
                                  key: employee.id,
                                  type: "button",
                                  disabled: _unref(form).employeeIds.includes(employee.id),
                                  onClick: ($event: any) => (addEmployee(employee))
                                }, _toDisplayString(employee.name), 9, _hoisted_24))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_25, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(selectedEmployees), (employee) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: employee.id
                            }, [
                              _createTextVNode(_toDisplayString(employee.name) + " ", 1),
                              _createElementVNode("button", {
                                type: "button",
                                onClick: ($event: any) => (removeEmployee(employee.id)),
                                "aria-label": _unref(t)('common.delete')
                              }, " × ", 8, _hoisted_26)
                            ]))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("fieldset", _hoisted_27, [
                    _createElementVNode("legend", null, _toDisplayString(_unref(t)("hr.assistantDocumentTypes")), 1),
                    _createElementVNode("p", null, _toDisplayString(_unref(t)("hr.assistantDocumentTypesHint")), 1),
                    _createElementVNode("label", _hoisted_28, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(allDocumentTypesSelected) ? (allDocumentTypesSelected).value = $event : null)),
                        type: "checkbox",
                        indeterminate: _unref(someDocumentTypesSelected),
                        disabled: !settings.value.documentTypes.length || saving.value
                      }, null, 8, _hoisted_29), [
                        [_vModelCheckbox, _unref(allDocumentTypesSelected)]
                      ]),
                      _createElementVNode("span", null, _toDisplayString(_unref(t)("hr.assistantSelectAllDocumentTypes")), 1)
                    ]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(settings.value.documentTypes, (type) => {
                      return (_openBlock(), _createElementBlock("label", {
                        key: type.id
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(form).documentTypeIds) = $event)),
                          type: "checkbox",
                          value: type.id
                        }, null, 8, _hoisted_30), [
                          [_vModelCheckbox, _unref(form).documentTypeIds]
                        ]),
                        _createElementVNode("span", null, _toDisplayString(type.name), 1)
                      ]))
                    }), 128)),
                    (!settings.value.documentTypes.length)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(_unref(t)("common.noData")), 1))
                      : _createCommentVNode("", true)
                  ])
                ], 64))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true)
  ]))
}
}

})